import type {
  CollaborationInvitePreview,
  CollaborationRole,
  NoteCollaborationInvite,
  NoteCollaborationState,
} from '../types/note';
import {
  SYNC_ENTITY_TYPES,
  type ServerSyncRecord,
  type SyncEntityType,
  type SyncMutationResult,
  type SyncPayload,
} from '../types/sync';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') ?? '';
const TOKEN_KEY = 'classmind.web.access-token';
const DEVICE_KEY = 'classmind.web.device-id';
const SESSION_KEY = 'classmind.web.session-snapshot';
const SYNC_TYPES_QUERY = SYNC_ENTITY_TYPES.join(',');

export interface SessionUser {
  id: string;
  email?: string | null;
  displayName?: string | null;
  isAnonymous: boolean;
}

export interface SessionSnapshot {
  accessToken: string;
  user: SessionUser;
  subscription?: {
    tier: string;
    storageQuotaBytes?: number;
  };
  storage?: {
    usedBytes: number;
    reservedBytes: number;
    quotaBytes: number;
    remainingBytes: number;
    breakdown?: Record<string, number>;
  };
}

interface APIErrorPayload {
  error?: string;
  message?: string;
  issues?: Array<{ path: string; message: string }>;
  requestedBytes?: number;
  storage?: SessionSnapshot['storage'];
}

export class APIError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload?: APIErrorPayload,
  ) {
    super(payload?.message || `请求失败（${status}）`);
    this.name = 'APIError';
  }
}

export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAccessToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getDeviceId() {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(DEVICE_KEY, created);
  return created;
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  requiresAuth = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  const token = getAccessToken();
  if (requiresAuth && token) headers.set('Authorization', `Bearer ${token}`);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  } catch {
    throw new APIError(0, { error: 'offline', message: '网络不可用，修改会保留在此设备' });
  }
  const payload = await response.json().catch(() => undefined) as APIErrorPayload | T | undefined;
  if (!response.ok) {
    if (response.status === 401) clearAccessToken();
    throw new APIError(response.status, payload as APIErrorPayload | undefined);
  }
  return payload as T;
}

export async function ensureSession(): Promise<SessionSnapshot> {
  if (import.meta.env.DEV) {
    const preview = await import('../test/ui-preview');
    if (preview.uiPreviewEnabled()) return preview.uiPreviewSession();
  }
  if (getAccessToken()) {
    try {
      const me = await apiRequest<Omit<SessionSnapshot, 'accessToken'>>('/v1/me');
      return rememberSession({ accessToken: getAccessToken()!, ...me });
    } catch (error) {
      if (error instanceof APIError && error.status === 0) {
        const cached = readRememberedSession();
        if (cached) return cached;
      }
      if (!(error instanceof APIError) || error.status !== 401) throw error;
    }
  }
  const session = await apiRequest<SessionSnapshot>(
    '/v1/auth/anonymous',
    {
      method: 'POST',
      body: JSON.stringify({
        deviceId: getDeviceId(),
        platform: 'web',
      }),
    },
    false,
  );
  localStorage.setItem(TOKEN_KEY, session.accessToken);
  return rememberSession(session);
}

export async function signInWithApple(identityToken: string): Promise<SessionSnapshot> {
  const session = await apiRequest<SessionSnapshot>('/v1/auth/apple', {
    method: 'POST',
    body: JSON.stringify({
      identityToken,
      deviceId: getDeviceId(),
      platform: 'web',
    }),
  });
  localStorage.setItem(TOKEN_KEY, session.accessToken);
  return rememberSession(session);
}

export async function signInWithGoogle(identityToken: string): Promise<SessionSnapshot> {
  const session = await apiRequest<SessionSnapshot>('/v1/auth/google', {
    method: 'POST',
    body: JSON.stringify({
      identityToken,
      deviceId: getDeviceId(),
      platform: 'web',
    }),
  });
  localStorage.setItem(TOKEN_KEY, session.accessToken);
  return rememberSession(session);
}

export async function signOut(): Promise<SessionSnapshot> {
  try {
    await apiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' });
  } finally {
    clearAccessToken();
  }
  localStorage.removeItem(SESSION_KEY);
  return ensureSession();
}

export async function deleteCurrentAccount(reason = 'web-user-requested'): Promise<SessionSnapshot> {
  await apiRequest<{ ok: true; deletionId: string }>('/v1/account', {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
  clearAccessToken();
  localStorage.removeItem(SESSION_KEY);
  return ensureSession();
}

export async function getSyncSnapshot(
  cursor?: string,
  after?: string,
): Promise<{
  snapshotCursor: string;
  records: ServerSyncRecord[];
  nextPageToken?: string | null;
  hasMore: boolean;
}> {
  const query = new URLSearchParams({
    limit: '200',
    types: SYNC_TYPES_QUERY,
    shared: '1',
  });
  if (cursor) query.set('cursor', cursor);
  if (after) query.set('after', after);
  return apiRequest(`/v1/sync/snapshot?${query}`);
}

export async function getSyncChanges(cursor: string): Promise<{
  changes: Array<{
    cursor: string;
    entityType: SyncEntityType;
    entityId: string;
    version: number;
    operation: 'upsert' | 'delete' | 'grant' | 'revoke';
    changedAt: string;
  }>;
  records: ServerSyncRecord[];
  nextCursor: string;
  hasMore: boolean;
}> {
  const query = new URLSearchParams({
    cursor,
    limit: '200',
    types: SYNC_TYPES_QUERY,
    shared: '1',
  });
  return apiRequest(`/v1/sync/changes?${query}`);
}

export async function getSyncRecord(entityType: SyncEntityType, entityId: string) {
  return apiRequest<ServerSyncRecord>(`/v1/sync/records/${entityType}/${entityId}`);
}

export async function pushSyncMutations(mutations: Array<{
  mutationId: string;
  entityType: SyncEntityType;
  entityId: string;
  operation: 'upsert' | 'delete';
  baseVersion: number;
  schemaVersion: number;
  payload?: SyncPayload;
  basePayload?: SyncPayload;
}>) {
  return apiRequest<{ results: SyncMutationResult[] }>('/v1/sync/mutations', {
    method: 'POST',
    body: JSON.stringify({ mutations }),
  });
}

export async function getNoteCollaboration(noteId: string) {
  return apiRequest<NoteCollaborationState>(`/v1/notes/${noteId}/collaboration`);
}

export async function enableCollaborationInvite(
  noteId: string,
  role: CollaborationRole,
) {
  return apiRequest<{ invite: NoteCollaborationInvite }>(
    `/v1/notes/${noteId}/collaboration/invite`,
    {
      method: 'POST',
      body: JSON.stringify({ role }),
    },
  );
}

export async function disableCollaborationInvite(noteId: string) {
  return apiRequest<{ invite: NoteCollaborationInvite | null }>(
    `/v1/notes/${noteId}/collaboration/invite`,
    { method: 'DELETE' },
  );
}

export async function updateCollaborationMember(
  noteId: string,
  collaboratorUserId: string,
  role: CollaborationRole,
) {
  return apiRequest<{ ok: boolean; role: CollaborationRole }>(
    `/v1/notes/${noteId}/collaboration/members/${encodeURIComponent(collaboratorUserId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    },
  );
}

export async function removeCollaborationMember(
  noteId: string,
  collaboratorUserId: string,
) {
  return apiRequest<{ ok: boolean }>(
    `/v1/notes/${noteId}/collaboration/members/${encodeURIComponent(collaboratorUserId)}`,
    { method: 'DELETE' },
  );
}

export async function leaveNoteCollaboration(noteId: string) {
  return apiRequest<{ ok: boolean }>(
    `/v1/notes/${noteId}/collaboration`,
    { method: 'DELETE' },
  );
}

export async function getCollaborationInvite(token: string) {
  return apiRequest<CollaborationInvitePreview>(
    `/v1/collaboration-invites/${encodeURIComponent(token)}`,
  );
}

export async function acceptCollaborationInvite(token: string) {
  return apiRequest<{
    noteId: string;
    role: CollaborationRole | 'owner';
    alreadyMember: boolean;
  }>(
    `/v1/collaboration-invites/${encodeURIComponent(token)}/accept`,
    { method: 'POST' },
  );
}

export async function getAssetDownload(assetId: string) {
  return apiRequest<{
    asset: {
      assetId: string;
      kind: string;
      mediaType: string;
      byteCount: number;
    };
    download: {
      url: string;
      expiresAt: string;
    };
  }>(`/v1/assets/${encodeURIComponent(assetId)}/download`);
}

export async function prepareAssetUpload(input: {
  assetId: string;
  entityType: SyncEntityType;
  entityId: string;
  kind: 'image' | 'drawing' | 'richTextRTF';
  mediaType: string;
  byteCount: number;
  checksumSha256: string;
}) {
  return apiRequest<{
    asset: {
      assetId: string;
      kind: string;
      mediaType: string;
      byteCount: number;
      checksumSha256: string;
      status: string;
    };
    upload: {
      url: string;
      method: 'PUT';
      headers: Record<string, string>;
      expiresAt: string;
    };
  }>('/v1/assets/uploads', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function completeAssetUpload(assetId: string) {
  return apiRequest<{
    asset: {
      assetId: string;
      kind: string;
      mediaType: string;
      byteCount: number;
      checksumSha256: string;
      status: string;
    };
  }>(`/v1/assets/${encodeURIComponent(assetId)}/complete`, {
    method: 'POST',
  });
}

function rememberSession(session: SessionSnapshot) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

function readRememberedSession(): SessionSnapshot | undefined {
  try {
    const value = localStorage.getItem(SESSION_KEY);
    if (!value) return undefined;
    const parsed = JSON.parse(value) as SessionSnapshot;
    return parsed.user?.id && parsed.accessToken ? parsed : undefined;
  } catch {
    return undefined;
  }
}
